import React, { useRef , useEffect } from 'react';
import { createPortal } from 'react-dom';

const BrandDropdown = ({ 
  brands, 
  brandId, 
  isBrandDropdownOpen, 
  setIsBrandDropdownOpen,
  brandSearchTerm,
  setBrandSearchTerm,
  isLoadingBrands,
  brandError,
  filteredBrands,
  handleOpenCreateBrandModal,
  setDropdownPosition,
  setBrandId
}) => {
  const brandDropdownRef = useRef(null);

  const clearSearchTerm = (e) => {
    e.stopPropagation();
    setBrandSearchTerm('');
  };

  const handleSelectBrand = (id) => {
    setBrandId(id);
    setTimeout(() => {
      setIsBrandDropdownOpen(false);
      setBrandSearchTerm('');
    }, 100);
  };

  const handleClearBrand = (e) => {
    e.stopPropagation();
    console.log('X button clicked - clearing brand');
    setBrandId(null);
  };

  useEffect(() => {
    const updatePosition = () => {
      if (brandDropdownRef.current && isBrandDropdownOpen) {
        const rect = brandDropdownRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isBrandDropdownOpen]);

  return (
    <div className="sm:col-span-2">
      <label htmlFor="brand" className="block text-sm font-medium text-gray-700 mb-1">
        Marque
      </label>
      <div className="relative" ref={brandDropdownRef}>
        <button
          type="button"
          className="bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
          onClick={() => setIsBrandDropdownOpen(!isBrandDropdownOpen)}
        >
          <span className="block truncate">
            {brandId && brands.find(b => b.id.toString() === brandId.toString())
              ? brands.find(b => b.id.toString() === brandId.toString()).name
              : 'Sélectionner une marque'}
          </span>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3z" clipRule="evenodd" />
              <path fillRule="evenodd" d="M10 17a1 1 0 01-.707-.293l-3-3a1 1 0 011.414-1.414L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3A1 1 0 0110 17z" clipRule="evenodd" />
            </svg>
          </span>
        </button>

        {brandId && (
          <div
            className="absolute inset-y-0 right-0 flex items-center pr-8 z-10 cursor-pointer"
            onClick={handleClearBrand}
          >
            <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        )}

        {isBrandDropdownOpen && createPortal(
          <div
            className="fixed z-50 bg-white shadow-lg rounded-md text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            style={{
              width: brandDropdownRef.current.offsetWidth,
              top: brandDropdownRef.current.getBoundingClientRect().bottom + window.scrollY,
              left: brandDropdownRef.current.getBoundingClientRect().left + window.scrollX
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-white">
              <input
                type="text"
                className="block w-full px-4 py-2 text-sm border-0 focus:ring-0 focus:outline-none"
                placeholder="Rechercher une marque..."
                value={brandSearchTerm}
                onChange={(e) => setBrandSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{ paddingRight: "28px" }}
              />
              {brandSearchTerm && (
                <button
                  type="button"
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-500"
                  onClick={clearSearchTerm}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              <div className="h-px bg-gray-200" />
            </div>

            <div className="max-h-52 overflow-y-auto">
              {isLoadingBrands ? (
                <div className="py-3 px-3 text-sm text-gray-500 flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Chargement des marques...
                </div>
              ) : brandError ? (
                <div className="py-2 px-3 text-sm text-red-500">{brandError}</div>
              ) : filteredBrands.length === 0 ? (
                <div className="py-2 px-3 text-sm text-gray-500">Aucune marque trouvée</div>
              ) : (
                filteredBrands.map((brand) => (
                  <div
                    key={brand.id}
                    className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100 ${brandId === brand.id ? 'bg-[#174540] bg-opacity-10 font-medium' : ''}`}
                    onClick={() => handleSelectBrand(brand.id)}
                  >
                    {brand.name}

                    {brandId === brand.id && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#174540]">
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="sticky bottom-0 border-t border-gray-200 bg-white mt-2">
              <button
                type="button"
                className="block w-full text-left px-4 py-2.5 text-sm text-[#174540] hover:bg-gray-50"
                onClick={handleOpenCreateBrandModal}
              >
                <div className="flex items-center">
                  <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Ajouter une marque
                </div>
              </button>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default BrandDropdown;